import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useGdprAdminAuth } from '../context/GdprAdminAuthContext';
import {
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowPathIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';

/**
 * Protected Route for GDPR Admin Pages
 *
 * Wraps GDPR Admin pages to ensure:
 * - User is authenticated via GDPR Admin auth
 * - Shows loading state while checking auth
 * - Redirects to login if not authenticated
 * - Displays session timeout warning banner
 */
const GdprAdminProtectedRoute = ({ children }) => {
  const {
    isAuthenticated,
    isLoading,
    timeoutWarning,
    getRemainingTime,
    extendSession,
    logout
  } = useGdprAdminAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <ArrowPathIcon className="h-12 w-12 animate-spin mx-auto text-[#0E3056] dark:text-[#FFC000]" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Verifying session...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/gdpradm/login" state={{ from: location }} replace />;
  }

  const remainingSeconds = getRemainingTime();
  const remainingMinutes = Math.floor(remainingSeconds / 60);
  const remainingSecondsDisplay = remainingSeconds % 60;

  return (
    <>
      {/* Session Timeout Warning Banner */}
      {timeoutWarning && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-500 text-yellow-900 py-3 px-4 shadow-lg">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-6 w-6 mr-3" />
              <div>
                <span className="font-semibold">Session Expiring Soon</span>
                <span className="ml-2">
                  Your session will expire in{' '}
                  <span className="font-mono font-bold">
                    {remainingMinutes}:{remainingSecondsDisplay.toString().padStart(2, '0')}
                  </span>
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={extendSession}
                className="flex items-center px-4 py-2 bg-yellow-700 text-white rounded-md hover:bg-yellow-800 transition-colors font-medium"
              >
                <ClockIcon className="h-4 w-4 mr-2" />
                Extend Session
              </button>
              <button
                onClick={() => logout()}
                className="flex items-center px-4 py-2 bg-yellow-900 text-white rounded-md hover:bg-yellow-950 transition-colors font-medium"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Status Bar (always visible) */}
      <div className="bg-[#0E3056] text-white py-2 px-4 text-sm">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <ClockIcon className="h-4 w-4 mr-2 text-[#FFC000]" />
            <span className="text-gray-300">Session expires in:</span>
            <span className="ml-2 font-mono font-semibold text-[#FFC000]">
              {remainingMinutes}:{remainingSecondsDisplay.toString().padStart(2, '0')}
            </span>
          </div>
          <button
            onClick={() => logout()}
            className="flex items-center text-gray-300 hover:text-white transition-colors"
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4 mr-1" />
            Logout
          </button>
        </div>
      </div>

      {/* Add padding when warning banner is shown */}
      <div className={timeoutWarning ? 'pt-12' : ''}>
        {children}
      </div>
    </>
  );
};

export default GdprAdminProtectedRoute;
